import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WidgetFooter from '../../components/WidgetFooter';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Leaf, Repeat, Wrench, ChevronRight, Loader2, X, Phone,
  Scissors, Lightbulb, TreeDeciduous, Flower2, Sparkles, Calendar
} from 'lucide-react';
import { widgetApi } from '../../lib/api';
import { getAssetUrl } from '../../lib/assetUrl';

// Map icon names to Lucide components
const iconMap = {
  'leaf': Leaf,
  'repeat': Repeat,
  'wrench': Wrench,
  'scissors': Scissors,
  'lightbulb': Lightbulb,
  'tree': TreeDeciduous,
  'flower': Flower2,
  'sparkles': Sparkles,
  'calendar': Calendar
};

// PricingHint - "Reste à charge" tag displayed under each dispatcher option.
// Extracted to module scope to avoid recreation on every render (React warning).
function PricingHint({ optionColor, hourlyRate, isTaxCreditEligible, textColor }) {
  if (!hourlyRate || hourlyRate <= 0) return null;
  if (isTaxCreditEligible) {
    const remaining = hourlyRate * 0.5;
    return (
      <div
        className="mt-3 -mx-1 rounded-xl p-3 flex items-center justify-between"
        style={{ backgroundColor: `${optionColor}10`, border: `1px dashed ${optionColor}55` }}
        data-testid="dispatcher-pricing-hint"
      >
        <div className="text-left">
          <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: `${textColor}80` }}>Reste à charge</p>
          <p className="text-2xl font-heading font-bold leading-tight" style={{ color: optionColor }}>
            {remaining.toFixed(2)}€
            <span className="text-xs font-normal opacity-70">/h</span>
          </p>
          <p className="text-[11px] mt-0.5" style={{ color: `${textColor}80` }}>
            Tarif de base : <span className="font-semibold">{hourlyRate.toFixed(2)}€/h</span>
          </p>
        </div>
        <div
          className="px-2.5 py-1 rounded-full text-[11px] font-bold text-white shadow-sm whitespace-nowrap"
          style={{ backgroundColor: optionColor }}
        >
          -50% crédit d&apos;impôt
        </div>
      </div>
    );
  }
  return (
    <div className="mt-3 text-left" data-testid="dispatcher-pricing-hint">
      <p className="text-sm font-medium" style={{ color: `${textColor}90` }}>
        À partir de <span className="font-bold" style={{ color: optionColor }}>{hourlyRate.toFixed(2)}€/h</span>
      </p>
    </div>
  );
}

export default function DispatcherWidget() {
  const { widgetId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await widgetApi.getDispatcherConfig(widgetId);
        setConfig(response.data);
      } catch (error) {
        console.error('Failed to load dispatcher widget:', error);
        toast.error('Impossible de charger le widget');
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [widgetId]);

  const getImageUrl = getAssetUrl;

  const handleOptionClick = (option) => {
    const { target_widget_id, target_widget_type } = option;
    if (!target_widget_id) {
      toast.error('Widget non configuré');
      return;
    }
    
    // Navigate to the appropriate widget based on type
    switch (target_widget_type) {
      case 'recurring':
        navigate(`/widget/recurring/${target_widget_id}`);
        break;
      case 'appointment':
        navigate(`/widget/appointment/${target_widget_id}`);
        break;
      case 'punctual':
      default:
        navigate(`/widget/punctual/${target_widget_id}`);
        break;
    }
  };

  // Legacy support for old dispatcher format (recurring_widget_id/punctual_widget_id)
  const handleRecurringChoice = () => {
    if (config?.widget?.recurring_widget_id) {
      navigate(`/widget/recurring/${config.widget.recurring_widget_id}`);
    } else {
      toast.error('Widget abonnement non configuré');
    }
  };

  const handlePunctualChoice = () => {
    if (config?.widget?.punctual_widget_id) {
      navigate(`/widget/punctual/${config.widget.punctual_widget_id}`);
    } else {
      toast.error('Widget ponctuel non configuré');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!config || !config.widget) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Leaf className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-lg font-medium">Widget indisponible</p>
            <p className="text-sm text-muted-foreground mt-2">
              Ce widget n&apos;est pas configuré ou n&apos;existe pas.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { widget, company, services = [] } = config;
  const primaryColor = widget.primary_color || '#2F5233';
  const bgColor = widget.bg_color || '#F9F8F6';
  const textColor = widget.text_color || '#1a1a1a';

  // ─── Tax-credit pricing hint ───
  // Show the company's hourly rate AND the "reste à charge" once 50% URSSAF
  // tax credit is deducted. Only when at least one active service is flagged
  // tax_credit_eligible (which is the default for cleaning / home help / etc).
  const defaultHourlyRate = Number(company.default_hourly_rate) || 0;
  const _anyTaxCreditEligible = services.some((s) => s.tax_credit_eligible !== false);
  
  // Get dispatcher options - use new format or fallback to legacy
  const dispatcherOptions = widget.dispatcher_options || [];
  const hasNewOptions = dispatcherOptions.length > 0;
  const hasLegacyOptions = widget.recurring_widget_id || widget.punctual_widget_id;

  // Sort options by order
  const sortedOptions = [...dispatcherOptions].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4 md:p-6"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className="w-full max-w-lg">
        <Card className="shadow-2xl border-0 overflow-hidden rounded-3xl">
          {/* Header with gradient */}
          <div 
            className="px-6 py-8 text-center text-white relative"
            style={{ 
              background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)` 
            }}
          >
            {/* Close button */}
            <button 
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors"
              onClick={() => window.history.back()}
              data-testid="dispatcher-close-btn"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Logo */}
            {company.logo_url && widget.show_logo !== false && (
              <img 
                src={getImageUrl(company.logo_url)}
                alt={company.name}
                className="h-16 mx-auto mb-4 object-contain" loading="lazy" decoding="async"
              />
            )}

            <h1 className="text-2xl font-bold mb-2">Comment pouvons-nous vous aider ?</h1>
            <p className="text-white/80 text-sm">
              Choisissez le service qui correspond à vos besoins
            </p>
          </div>

          <CardContent className="p-6">
            <div className="space-y-3">
              {/* New dispatcher options format */}
              {hasNewOptions && sortedOptions.map((option) => {
                const IconComponent = iconMap[option.icon] || Leaf;
                const optionColor = option.color || '#22C55E';
                const bgColorLight = `${optionColor}15`;
                
                return (
                  <button
                    key={option.id}
                    onClick={() => handleOptionClick(option)}
                    className="w-full flex flex-col p-4 rounded-2xl border-2 text-left transition-all hover:shadow-lg hover:scale-[1.02] group relative"
                    style={{ 
                      borderColor: optionColor, 
                      backgroundColor: bgColorLight 
                    }}
                    data-testid={`dispatcher-option-${option.id}`}
                  >
                    <div className="flex items-center gap-4 w-full">
                      {/* Icon */}
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${optionColor}25` }}
                      >
                        <IconComponent className="w-7 h-7" style={{ color: optionColor }} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-lg truncate">{option.title}</p>
                        {option.subtitle && (
                          <p className="text-sm text-muted-foreground truncate">{option.subtitle}</p>
                        )}
                      </div>

                      {/* Badge if present */}
                      {option.badge_text && (
                        <div 
                          className="absolute -top-2 right-4 px-3 py-1 rounded-full text-xs font-semibold text-white shadow-md"
                          style={{ backgroundColor: option.badge_color || '#F59E0B' }}
                        >
                          {option.badge_text}
                        </div>
                      )}

                      {/* Arrow */}
                      <ChevronRight 
                        className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" 
                        style={{ color: optionColor }}
                      />
                    </div>
                    <PricingHint optionColor={optionColor} hourlyRate={defaultHourlyRate} isTaxCreditEligible={_anyTaxCreditEligible} textColor={textColor} />
                  </button>
                );
              })}

              {/* Legacy format fallback */}
              {!hasNewOptions && hasLegacyOptions && (
                <>
                  {/* Option Récurrent - Abonnement */}
                  {widget.recurring_widget_id && (
                    <button
                      onClick={handleRecurringChoice}
                      className="w-full flex flex-col p-4 rounded-2xl border-2 text-left transition-all hover:shadow-lg hover:scale-[1.02] group"
                      style={{ borderColor: '#22C55E', backgroundColor: '#F0FDF4' }}
                      data-testid="dispatcher-recurring-btn"
                    >
                      <div className="flex items-center gap-4 w-full">
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-green-100">
                          <Repeat className="w-7 h-7 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-lg">Entretien Récurrent</p>
                          <p className="text-sm text-muted-foreground">
                            Abonnement mensuel, entretien régulier
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-green-600 group-hover:translate-x-1 transition-transform" />
                      </div>
                      <PricingHint optionColor="#22C55E" hourlyRate={defaultHourlyRate} isTaxCreditEligible={_anyTaxCreditEligible} textColor={textColor} />
                    </button>
                  )}

                  {/* Option Ponctuel */}
                  {widget.punctual_widget_id && (
                    <button
                      onClick={handlePunctualChoice}
                      className="w-full flex flex-col p-4 rounded-2xl border-2 text-left transition-all hover:shadow-lg hover:scale-[1.02] group"
                      style={{ borderColor: '#3B82F6', backgroundColor: '#EFF6FF' }}
                      data-testid="dispatcher-punctual-btn"
                    >
                      <div className="flex items-center gap-4 w-full">
                        <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-blue-100">
                          <Wrench className="w-7 h-7 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-lg">Intervention Ponctuelle</p>
                          <p className="text-sm text-muted-foreground">
                            Une intervention unique, à la demande
                          </p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
                      </div>
                      <PricingHint optionColor="#3B82F6" hourlyRate={defaultHourlyRate} isTaxCreditEligible={_anyTaxCreditEligible} textColor={textColor} />
                    </button>
                  )}
                </>
              )}

              {/* No options configured */}
              {!hasNewOptions && !hasLegacyOptions && (
                <div className="text-center py-8 text-muted-foreground">
                  <Leaf className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune option configurée</p>
                  <p className="text-sm">Contactez l&apos;entreprise pour plus d&apos;informations.</p>
                </div>
              )}
            </div>

            {/* Phone number */}
            {company.phone && (
              <div className="mt-6 pt-4 border-t text-center">
                <p className="text-sm text-muted-foreground mb-2">Besoin d&apos;aide ?</p>
                <a 
                  href={`tel:${company.phone}`}
                  className="inline-flex items-center gap-2 font-semibold hover:underline"
                  style={{ color: primaryColor }}
                >
                  <Phone className="w-4 h-4" />
                  {company.phone}
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer - Propulsé par ResaWork + Widget ID */}
        <WidgetFooter widgetId={widgetId} isPremium={config?.is_premium} />
      </div>
    </div>
  );
}
